import { CommonModule, Location } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  inject,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

import {
  CmsUrl,
  HeaderResponse,
  NavMenuColumn,
} from '@digital-platform/rs-cms/domain';
import {
  CtaButtonComponent,
  NavigationHelperService,
  RichTextComponent,
  RichTextLinkComponent,
} from '@digital-platform/rs-cms/feature-ptypes';
import { DesktopMenuComponent } from '../desktop-menu/desktop-menu.component';
import { InlineImageNavComponent } from '../inline-image-nav/inline-image-nav.component';

@Component({
  selector: 'digital-platform-nav-dropdown',
  templateUrl: './nav-dropdown.component.html',
  styleUrls: ['./nav-dropdown.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    RichTextLinkComponent,
    RichTextComponent,
    InlineImageNavComponent,
    CtaButtonComponent,
    DesktopMenuComponent,
  ],
})
export class NavDropdownComponent {
  private location: Location = inject(Location);
  private readonly navigationHelperService = inject(NavigationHelperService);

  @Input() activeFirstMenu: NavMenuColumn[] | null = null;
  @Output() mobileNavOpenChange = new EventEmitter<boolean>();
  @Input() mobileNavOpen = false;
  @Input() menuKeys: string[] = [];
  @Input() icons: string[] = [];
  @Input() config!: HeaderResponse[0];
  @Output() mobileHeaderTitle = new EventEmitter<string | null>();
  @Output() stateReset = new EventEmitter<void>();
  activeSecondaryMenu!: any;
  activeTertiaryMenu!: any;
  public viewAllLink: CmsUrl | null = null;

  constructor(private router: Router) {}

  ngOnInit() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.resetState();
        this.mobileHeaderTitle.emit(null);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      !changes['mobileNavOpen']?.currentValue &&
      changes['mobileNavOpen']?.previousValue
    ) {
      this.resetState();
    }
  }

  shouldNavBack() {
    if (this.activeTertiaryMenu) {
      this.activeTertiaryMenu = null;
      this.mobileHeaderTitle.emit(this.activeSecondaryMenu.title);
    } else if (this.activeSecondaryMenu) {
      this.activeSecondaryMenu = null;
      this.mobileHeaderTitle.emit(null);
    }
  }

  resetState(): void {
    this.activeFirstMenu = null;
    this.activeSecondaryMenu = null;
    this.activeTertiaryMenu = null;
    this.stateReset.emit();
  }

  getMenuItem(key: string) {
    return (this.config as { [key: string]: any })[key];
  }

  onSetActiveMenu(
    menu: 'activeFirstMenu' | 'activeSecondaryMenu' | 'activeTertiaryMenu',
    value: any,
    pageTitle: string,
    viewAllLink?: string
  ) {
    this[menu] = { columns: value, title: pageTitle };

    this.viewAllLink = viewAllLink
      ? [{ uri: viewAllLink, options: { attributes: { target: 0 } } }]
      : null;
    if (menu === 'activeSecondaryMenu' || menu === 'activeTertiaryMenu') {
      this.mobileHeaderTitle.emit(pageTitle);
    } else {
      this.mobileHeaderTitle.emit(null);
    }
  }

  closeMenuIfNeeded() {
    if (this.viewAllLink?.[0]?.uri === this.location.path()) {
      this.mobileNavOpenChange.emit((this.mobileNavOpen = false));
      this.resetState();
    }
  }

  getTarget(
    target: string | undefined,
    url: string | null
  ): '_blank' | '_self' | undefined {
    let shouldOpenNewTab = false;

    if (target) {
      shouldOpenNewTab = target === '_blank';
    } else {
      shouldOpenNewTab =
        this.navigationHelperService.isSupportedFile(url || '') ||
        this.navigationHelperService.isExternalUrl(url || '');
    }
    return shouldOpenNewTab ? '_blank' : '_self';
  }
}
